// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';

import { RowDataChangedEvent } from 'wdna-grid-table/lib/types';
import Table, { TableButtonsConfig } from 'wdna-components/lib/table/Table';
import { TabConfig, addTab, removeTab } from 'wdna-components/lib/tabs';
import { messageInfo, messageError, messageBoxWarning } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';

import TypesEdit from '../forms/sensortypes/Edit';
import SensorTypeVariables from './SensorTypeVariables';
import SensorTypeParameters from './SensorTypeParameters';

import { typeSensor, TypeSensorListDO } from '../../requests';

type Props = unknown;

interface State {
  loading: boolean;
  update: string;
  showGrid: boolean;

  data: TypeSensorListDO[];
}

export default class SensorTypes extends React.Component<Props, State> {
  private tabId: string;
  private buttons: TableButtonsConfig;
  private mounted: boolean;

  public constructor(props: Props) {
    super(props);

    this.tabId = window.getStr();
    this.mounted = false;

    this.buttons = {
      edit: true,
      add: true,
      del: true,
      list: true,
      list2: true,
      toggleItem: true,
      labels: {
        list: 'Variables',
        list2: 'Parameters'
      }
    };

    this.state = {
      update: window.getStr(),
      loading: false,
      data: [],

      showGrid: false,
    };
  }

  public componentDidMount = (): void => {
    this.mounted = true;
    this.load();
  };

  public shouldComponentUpdate = (nextProps: Props, nextState: State): boolean => {
    return this.state.update !== nextState.update;
  }

  public componentWillUnmount = (): void => {
    this.mounted = false;
  };

  public load = (): void => {
    this.loadingStart();
    typeSensor<TypeSensorListDO[]>('list')
      ?.then((resp: TypeSensorListDO[]) => {
        if (this.mounted) {
          this.setState({
            data: resp,
          });
        }
      })
      .catch((error: any) => {
        messageError('Error', getErrorMessage(error));
      })
      .finally(() => {
        this.loadingFinish();
      });
  };

  public loadingStart = (): void => {
    this.setState({
      update: window.getStr(),
      loading: true,
    });
  };

  public loadingFinish = (): void => {
    this.setState({
      update: window.getStr(),
      loading: false,
    });
  };

  public loadTab = (rowId?: number): void => {
    const tabId: string = 'STE';
    const title: string = rowId ? `Sensor-Type Edit: ${rowId}` : 'Sensor-Type New';

    window.store.dispatch(removeTab(tabId));
    const tabData: TabConfig = {
      id: tabId,
      active: true,
      title,
      component: TypesEdit,
      props: {
        rowId,
        tabId: this.tabId,
        report: 'SENSOR TYPES',
        title: 'Sensor Types',
        notify: this.notify,
      },
    };
    window.store.dispatch(addTab(tabData));
  };

  public notify = (): void => {
    this.load();
  };

  public onEdit = (value: TypeSensorListDO): void => {
    window.store.dispatch(removeTab('STE'));
    const rowId: number = value?.id;
    setTimeout(() => {
      this.loadTab(rowId);
    }, 0);
  };

  public onDelete = (value: TypeSensorListDO): void => {
    messageBoxWarning('Delete', 'Do you want to delete this register?', () => {
      this.loadingStart();
      typeSensor('del', String(value.id))
        ?.then(() => {
          messageInfo('Success', 'Register successfully deleted', 5000);
          this.load();
        })
        .catch((error: any) => {
          messageError('Error', getErrorMessage(error));
        })
        .finally(() => {
          this.loadingFinish();
        });
    });
  };

  public onList = (value: TypeSensorListDO): void => {
    window.store.dispatch(removeTab('STV'));
    const rowId: number = value.id;
    setTimeout(() => {
      this.loadTabVariables(rowId);
    }, 0);
  };

  public loadTabVariables = (rowId: number): void => {
    const tabId: string = 'STV';
    const tabData: TabConfig = {
      id: tabId,
      active: true,
      title: `Variables | Sensor Type ID: ${rowId}`,
      component: SensorTypeVariables,
      props: {
        rowId,
        tabId: this.tabId,
        report: 'SENSOR TYPE VARIABLES',
        title: 'Sensor Type Variables',
      },
    };
    window.store.dispatch(addTab(tabData));
  };

  public onList2 = (value: TypeSensorListDO): void => {
    window.store.dispatch(removeTab('STP'));
    const rowId: number = value.id;
    setTimeout(() => {
      this.loadTabParameters(rowId);
    }, 0);
  };

  public loadTabParameters = (rowId: number): void => {
    const tabId: string = 'STP';
    const tabData: TabConfig = {
      id: tabId,
      active: true,
      title: `Parameters | Sensor Type ID: ${rowId}`,
      component: SensorTypeParameters,
      props: {
        rowId,
        tabId: this.tabId,
        report: 'SENSOR TYPE PARAMETERS',
        title: 'Sensor Type Parameters',
      },
    };
    window.store.dispatch(addTab(tabData));
  };

  public rowDataChanged = (_event: RowDataChangedEvent): void => {
    this.setState({
      update: window.getStr(),
      showGrid: true,
    });
  };

  public render(): JSX.Element {
    const { loading, data, showGrid } = this.state;

    const columnAlias: Record<string, string> = {
      id: 'ID',
      code: 'Code',
      name: 'Name',
      type: 'Type',
      vers: 'Version',
      active: 'Active',
      typeModuleName: 'Module Type',
      Actions: 'Action Buttons',
    };
    const columnsToHide: string[] = [
      'typeModuleId'
    ];
    const columnStyles: Record<string, any> = {
      id: { textAlign: 'right' },
      code: { textAlign: 'right' },
      name: { textAlign: 'left' },
      type: { textAlign: 'left' },
      vers: { textAlign: 'right' },
      active: { textAlign: 'center' },
      typeModuleName: { textAlign: 'left' },
      actionButtons: { textAlign: 'center' },
    };

    return (
      <div id="content" className="content" style={{ visibility: (showGrid ? 'visible' : 'hidden') }}>
        <Table
          data={data}
          alias={columnAlias}
          customCellStyles={columnStyles}
          hiddenCols={columnsToHide}
          buttons={this.buttons}
          onEdit={this.onEdit}
          onAdd={this.onEdit}
          onDelete={this.onDelete}
          onList={this.onList}
          onList2={this.onList2}
          loading={loading}
          rowDataChanged={this.rowDataChanged}
          pagination={false}
          showRowNumber
        />
      </div>
    );
  }
}
